import type { CombinedError } from '@urql/core';

import type { AuthAccountSwitchedMessage, AuthAccountSwitchInFlight } from '~/constants/auth';
import type {
  ChooseAccountInput,
  ResetPasswordAfterCodeInput,
  SwitchAccountInput,
} from '~/graphql/generated/graphql';
import type { SignInContinuation } from '~/types/login';

import { AUTH_ACCOUNT_SWITCH_IN_FLIGHT_STATE, AUTH_ACCOUNT_SWITCHED_HERE_STATE, AUTH_ACCOUNT_SWITCHED_MESSAGE, AUTH_BROADCAST_CHANNEL } from '~/constants/auth';
import {
  CHOOSE_ACCOUNT_MUTATION,
  PENDING_ACCOUNT_CHOICES_QUERY,
  RESET_PASSWORD_AFTER_CODE_MUTATION,
  SIGN_IN_AWAITS_PASSWORD_QUERY,
  SWITCH_ACCOUNT_MUTATION,
} from '~/graphql/documents/core';
import { holdOtherTabsForAccountSwitch } from '~/utils/accountSwitchTabs';

interface AccountSwitch {
  input: SwitchAccountInput;
  schoolName: string;
  landingPath: string;
}

const toSignInResult = (payload: SignInContinuation | null | undefined, error: CombinedError | undefined) => {
  if (error || !payload?.flow) {
    return { flow: null, hasMatchedInvite: false, passwordChangeToken: null, error: error ?? 'An unknown error occurred' };
  }

  return {
    flow: payload.flow,
    hasMatchedInvite: payload.hasMatchedInvite,
    passwordChangeToken: payload.passwordChangeToken ?? null,
    error: null,
  };
};

/** The schools site's sign-in after the code: a forgotten password, the choice of school account, and the switch between them. */
export const useSchoolsSignIn = () => {
  const { $urqlClient, $operationGate } = useNuxtApp();
  const switchedHere = useState<boolean>(AUTH_ACCOUNT_SWITCHED_HERE_STATE, () => false);
  const switchingAccount = useState<AuthAccountSwitchInFlight | null>(AUTH_ACCOUNT_SWITCH_IN_FLIGHT_STATE, () => null);

  const resetPasswordAfterCode = async (input: ResetPasswordAfterCodeInput) => {
    const res = await $urqlClient.mutation(RESET_PASSWORD_AFTER_CODE_MUTATION, { input });
    return toSignInResult(res.data?.core.auth.resetPasswordAfterCode, res.error);
  };

  const pendingAccountChoices = async () => {
    const res = await $urqlClient.query(PENDING_ACCOUNT_CHOICES_QUERY, {}, { requestPolicy: 'network-only' }).toPromise();

    if (res.error) {
      return { choices: [], error: res.error };
    }

    return { choices: res.data?.core.auth.pendingAccountChoices ?? [], error: null };
  };

  const signInAwaitsPassword = async (email: string) => {
    const res = await $urqlClient.query(SIGN_IN_AWAITS_PASSWORD_QUERY, { email }, { requestPolicy: 'network-only' }).toPromise();

    return { awaitsPassword: res.data?.core.auth.signInAwaitsPassword ?? false, error: res.error ?? null };
  };

  /** A refused pick that would have finished an integration provider's sign-in also names that provider's failure screen. */
  const chooseAccount = async (input: ChooseAccountInput) => {
    const res = await $urqlClient.mutation(CHOOSE_ACCOUNT_MUTATION, { input });
    const payload = res.data?.core.auth.chooseAccount;
    return { ...toSignInResult(payload, res.error), providerLoginFailure: payload?.providerLoginFailure ?? null };
  };

  /**
   * A response that left on the old session would write that session's cookie back over the new one,
   * so the switch waits for those to answer, in this tab and the other open ones, and nothing else goes
   * out until it is done. A successful switch keeps requests held: the caller loads the landing page in
   * full, and the other tabs reload on the switched message.
   */
  const switchAccount = async ({ input, schoolName, landingPath }: AccountSwitch) => {
    switchingAccount.value = { schoolName };
    const otherTabs = holdOtherTabsForAccountSwitch();
    await Promise.all([$operationGate.hold(), otherTabs.settled]);
    const res = await $urqlClient.mutation(SWITCH_ACCOUNT_MUTATION, { input }, { passesOperationGate: true });

    if (res.error) {
      otherTabs.release();
      $operationGate.release();
      switchingAccount.value = null;
      return { error: res.error };
    }

    // The session every tab shares is now the other account's; the tabs still showing the old one move on.
    switchedHere.value = true;
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      const message: AuthAccountSwitchedMessage = { type: AUTH_ACCOUNT_SWITCHED_MESSAGE, path: landingPath };
      channel.postMessage(message);
      channel.close();
    }

    return { error: null };
  };

  return {
    resetPasswordAfterCode,
    pendingAccountChoices,
    signInAwaitsPassword,
    chooseAccount,
    switchAccount,
    switchingAccount: readonly(switchingAccount),
  };
};
